import React, { useEffect, useMemo, useState } from "react";
import LayerAdmin from "../LayerAdmin";
import { toast } from "react-toastify";
import ButtonLoader from "../../zamzam/components/ButtonLoader";
import { useNavigate, useParams } from "react-router-dom";
import URL_MAPPING from "../../../routes/constants";
import {
  getTrainingVideoById,
  updateTrainingVideo,
} from "../api/trainingVideos";
import { getGlobalFilter } from "../../../api/filters";
import { delivery_man, preseller, salesman, sdr } from "../../../utility/constant";
import Dropdown from "../components/Dropdown";
import Loader from "../../../components/Loader";

type VideoFileKey = "file" | "file_ar" | "file_ur" | "file_hi" | "file_bangla";
type VideoLanguage = "en" | "ar" | "ur" | "hi" | "bangla";

const LANGUAGE_OPTIONS: {
  value: VideoLanguage;
  label: string;
  field: VideoFileKey;
}[] = [
  { value: "en", label: "English", field: "file" },
  { value: "ar", label: "Arabic", field: "file_ar" },
  { value: "ur", label: "Urdu", field: "file_ur" },
  { value: "hi", label: "Hindi", field: "file_hi" },
  { value: "bangla", label: "Bangla", field: "file_bangla" },
];

const toDateInputValue = (date?: string | null) => {
  if (!date) return "";
  return date.split("T")[0];
};

const parseScheduleEntries = (data: unknown): string[] => {
  if (!data) return [];

  try {
    const entries =
      typeof data === "string" ? JSON.parse(data) : data;

    if (!Array.isArray(entries)) return [];

    return entries
      .map((entry) => entry?.date)
      .filter((date): date is string => Boolean(date));
  } catch {
    return [];
  }
};

const parseSalesOffices = (data: unknown): string[] => {
  if (!data) return [];

  try {
    const offices = typeof data === "string" ? JSON.parse(data) : data;
    return Array.isArray(offices) ? offices.map(String) : [];
  } catch {
    return [];
  }
};

const parseChannels = (data: unknown): string[] => {
  if (!data) return [];

  try {
    const channelValues = typeof data === "string" ? JSON.parse(data) : data;
    return Array.isArray(channelValues) ? channelValues.map(String) : [];
  } catch {
    return [];
  }
};

const EditTrainingVideo = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [salesOffices, setSalesOffices] = useState<any[]>([]);
  const [channels, setChannels] = useState<any[]>([]);
  const [selectedSalesOffices, setSelectedSalesOffices] = useState<string[]>([]);
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<VideoLanguage>("en");
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [scheduleDates, setScheduleDates] = useState<string[]>([]);
  const [pendingScheduleDate, setPendingScheduleDate] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    video_start_date: "",
    video_end_date: "",
  });

  useEffect(() => {
    const fetchGlobalFilters = async () => {
      try {
        setLoadingFilters(true);
        const response = await getGlobalFilter({
          regions: ["SOUTH", "Riyadh Plastic", "CENTRAL", "EAST", "NORTH", "WEST"],
          positions: [salesman, preseller, sdr, delivery_man],
        });
        setSalesOffices(response?.sales_offices || []);
        setChannels(response?.channels || []);
      } catch (error) {
        console.error("Failed to fetch sales offices:", error);
      } finally {
        setLoadingFilters(false);
      }
    };

    fetchGlobalFilters();
  }, []);

  useEffect(() => {
    const fetchVideo = async () => {
      if (!id) return;

      try {
        setLoadingData(true);
        const data = await getTrainingVideoById(id);

        setFormData({
          title: data?.title || "",
          video_start_date: toDateInputValue(data?.video_start_date),
          video_end_date: toDateInputValue(data?.video_end_date),
        });
        setScheduleDates(parseScheduleEntries(data?.schedule_entries));
        setSelectedSalesOffices(parseSalesOffices(data?.sales_offices));
        setSelectedChannels(parseChannels(data?.channels));
      } catch (error: any) {
        console.error("Failed to fetch training video:", error);
        const detail = error?.response?.data?.detail;
        if (Array.isArray(detail)) {
          detail.forEach((err) => toast.error(err.msg));
        } else {
          toast.error(detail?.msg || detail || "Failed to fetch training video.");
        }
      } finally {
        setLoadingData(false);
      }
    };

    fetchVideo();
  }, [id]);

  useEffect(() => {
    if (!formData.video_start_date || !formData.video_end_date) return;

    setScheduleDates((prev) =>
      prev.filter(
        (date) =>
          date >= formData.video_start_date && date <= formData.video_end_date
      )
    );
  }, [formData.video_start_date, formData.video_end_date]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (name === "video_end_date" && formData.video_start_date && value < formData.video_start_date) {
      toast.error("End date cannot be before start date.");
      return;
    }

    if (name === "video_start_date" && formData.video_end_date && value > formData.video_end_date) {
      toast.error("Start date cannot be after end date.");
      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  const canPickScheduleDates =
    Boolean(formData.video_start_date) && Boolean(formData.video_end_date);

  const addScheduleDate = () => {
    if (!canPickScheduleDates) {
      toast.error("Please set video start and end dates first.");
      return;
    }

    if (!pendingScheduleDate) {
      toast.error("Please select a schedule date.");
      return;
    }

    if (
      pendingScheduleDate < formData.video_start_date ||
      pendingScheduleDate > formData.video_end_date
    ) {
      toast.error("Schedule date must be between start and end date.");
      return;
    }

    if (scheduleDates.includes(pendingScheduleDate)) {
      toast.error("This date is already scheduled.");
      return;
    }

    setScheduleDates((prev) => [...prev, pendingScheduleDate].sort());
    setPendingScheduleDate("");
  };

  const removeScheduleDate = (date: string) => {
    setScheduleDates((prev) => prev.filter((d) => d !== date));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const validTypes = ["video/mp4", "video/webm", "video/quicktime"];
    const maxSize = 500 * 1024 * 1024;

    if (
      !validTypes.includes(selectedFile.type) &&
      !selectedFile.name.match(/\.(mp4|webm|mov)$/i)
    ) {
      toast.error("Invalid file type. Only MP4, WEBM, and MOV are allowed.");
      return;
    }

    if (selectedFile.size > maxSize) {
      toast.error("File size exceeds the maximum limit of 500MB.");
      return;
    }

    setVideoFile(selectedFile);
  };

  const getSelectedFileField = (): VideoFileKey => {
    return (
      LANGUAGE_OPTIONS.find((lang) => lang.value === selectedLanguage)?.field ??
      "file"
    );
  };

  const salesOfficeOptions = useMemo(
    () =>
      salesOffices.map((office: any) => {
        const officeId = String(office.id ?? office.sales_office_id);
        const officeName = office.name ?? office.sales_office_name ?? officeId;
        return {
          value: officeId,
          name: `${officeName} (${officeId})`,
        };
      }),
    [salesOffices]
  );

  const channelOptions = useMemo(
    () =>
      channels.map((channel: any) => {
        const channelId = String(channel.id ?? channel.channel_id);
        const channelName = channel.name ?? channel.channel_name ?? channelId;
        return {
          value: channelId,
          name: `${channelName} (${channelId})`,
        };
      }),
    [channels]
  );

  const handleSalesOfficeChange = (e: {
    target: { name: string; value: string | string[] };
  }) => {
    setSelectedSalesOffices(
      Array.isArray(e.target.value) ? e.target.value : [e.target.value]
    );
  };

  const handleChannelChange = (e: {
    target: { name: string; value: string | string[] };
  }) => {
    setSelectedChannels(
      Array.isArray(e.target.value) ? e.target.value : [e.target.value]
    );
  };

  const getInputClass = (field: string) =>
    `w-full border px-3 py-2 rounded ${
      invalidFields.includes(field) ? "border-red-500" : ""
    }`;

  const appendOptionalField = (
    form: FormData,
    key: string,
    value: string | number | undefined
  ) => {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      form.append(key, String(value));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!id) {
      toast.error("Invalid training video id.");
      return;
    }

    const emptyFields: string[] = [];
    if (!formData.title.trim()) emptyFields.push("title");

    setInvalidFields(emptyFields);
    if (emptyFields.length > 0) {
      toast.error("Please fill all required fields.");
      return;
    }

    const form = new FormData();

    if (videoFile) {
      const fileField = getSelectedFileField();
      form.append(fileField, videoFile, videoFile.name);
    }

    form.append("title", formData.title.trim());
    form.append(
      "schedule_entries",
      JSON.stringify(scheduleDates.map((date) => ({ date })))
    );
    appendOptionalField(form, "video_start_date", formData.video_start_date);
    appendOptionalField(form, "video_end_date", formData.video_end_date);
    form.append("sales_offices", JSON.stringify(selectedSalesOffices));
    form.append("channels", JSON.stringify(selectedChannels));

    try {
      setLoading(true);
      const response = await updateTrainingVideo(id, form);
      toast.success(response?.message || "Training video updated successfully.");
      setTimeout(() => {
        navigate(URL_MAPPING.TRAININGVIDEOS);
      }, 2000);
    } catch (error: any) {
      console.error("Failed to update training video:", error);
      const detail = error?.response?.data?.detail;
      if (Array.isArray(detail)) {
        detail.forEach((err) => toast.error(err.msg));
      } else {
        toast.error(detail?.msg || detail || "Failed to update training video.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <LayerAdmin>
        <div className="flex justify-center items-center h-64">
          <Loader />
        </div>
      </LayerAdmin>
    );
  }

  return (
    <LayerAdmin>
      <div className="flex items-center px-5 pt-5 gap-3">
        <button
          type="button"
          onClick={() => window.history.back()}
          className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
        >
          Back
        </button>
        <h3 className="text-xl font-bold text-gray-700">Edit Training Video</h3>
      </div>

      <form onSubmit={handleSubmit} className="p-5 lg:w-2/3 sm:w-full space-y-4">
        <div>
          <label className="block font-semibold mb-1">Language</label>
          <select
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value as VideoLanguage)}
            className={getInputClass("language")}
          >
            {LANGUAGE_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-500 mt-1">
            Select language only when replacing the video file.
          </p>
        </div>

        <div>
          <label className="block font-semibold mb-1">Replace Video File</label>
          <input
            type="file"
            accept=".mp4,.webm,.mov,video/mp4,video/webm,video/quicktime"
            onChange={handleFileChange}
            className={getInputClass("video")}
          />
          {videoFile?.name && (
            <p className="text-sm text-gray-600 mt-1">
              {videoFile.name} (
              {LANGUAGE_OPTIONS.find((l) => l.value === selectedLanguage)?.label})
            </p>
          )}
        </div>

        <div>
          <label className="block font-semibold mb-1">Title *</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className={getInputClass("title")}
          />
        </div>

        <div className="flex gap-4">
          <div className="w-1/2">
            <label className="block font-semibold mb-1">Video Start Date</label>
            <input
              type="date"
              name="video_start_date"
              value={formData.video_start_date}
              max={formData.video_end_date || undefined}
              onChange={handleChange}
              className={getInputClass("video_start_date")}
            />
          </div>
          <div className="w-1/2">
            <label className="block font-semibold mb-1">Video End Date</label>
            <input
              type="date"
              name="video_end_date"
              value={formData.video_end_date}
              min={formData.video_start_date || undefined}
              onChange={handleChange}
              className={getInputClass("video_end_date")}
            />
          </div>
        </div>

        <div>
          <label className="block font-semibold mb-1">Schedule Entries</label>
          <div className="flex gap-2">
            <input
              type="date"
              value={pendingScheduleDate}
              min={formData.video_start_date || undefined}
              max={formData.video_end_date || undefined}
              disabled={!canPickScheduleDates}
              onChange={(e) => setPendingScheduleDate(e.target.value)}
              className={`${getInputClass("schedule_entries")} flex-1 disabled:bg-gray-100 disabled:cursor-not-allowed`}
            />
            <button
              type="button"
              onClick={addScheduleDate}
              disabled={!canPickScheduleDates}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              Add Date
            </button>
          </div>
          {!canPickScheduleDates && (
            <p className="text-sm text-gray-500 mt-1">
              Set video start and end dates to add schedule entries.
            </p>
          )}
          {scheduleDates.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {scheduleDates.map((date) => (
                <span
                  key={date}
                  className="inline-flex items-center gap-1 bg-teal-50 text-teal-800 border border-teal-200 rounded px-2 py-1 text-sm"
                >
                  {date}
                  <button
                    type="button"
                    onClick={() => removeScheduleDate(date)}
                    className="text-teal-600 hover:text-teal-900 font-bold leading-none"
                    aria-label={`Remove ${date}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="block font-semibold mb-1">Sales Offices</label>
          <Dropdown
            name="sales_offices"
            value={selectedSalesOffices}
            onChange={handleSalesOfficeChange}
            options={salesOfficeOptions}
            className={getInputClass("sales_offices")}
            placeholder={
              loadingFilters ? "Loading sales offices..." : "Select sales offices"
            }
            searchable
            multiple
            disabled={loadingFilters || salesOfficeOptions.length === 0}
          />
        </div>

        <div>
          <label className="block font-semibold mb-1">Channels</label>
          <Dropdown
            name="channels"
            value={selectedChannels}
            onChange={handleChannelChange}
            options={channelOptions}
            className={getInputClass("channels")}
            placeholder={
              loadingFilters ? "Loading channels..." : "Select channels"
            }
            searchable
            multiple
            disabled={loadingFilters || channelOptions.length === 0}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="bg-teal-500 text-white px-6 py-2 rounded hover:bg-teal-600 flex items-center gap-2"
        >
          Update Video {loading && <ButtonLoader />}
        </button>
      </form>
    </LayerAdmin>
  );
};

export default EditTrainingVideo;
